// File: frontend/src/pages/reports/StaffReport.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSchool } from '../../context/SchoolContext';
import { exportToPDF } from '../../utils/pdfExport';
import api from '../../utils/axios';

const StaffReportPage = () => {
  const { school } = useSchool();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get('/api/v1/reports/staff');
        if (res.data?.success) {
          setData(res.data.data);
        } else {
          setError(res.data?.message || 'Failed to load staff report.');
        }
      } catch (err) {
        console.error('Error loading staff report:', err);
        setError(err.response?.data?.message || err.message || 'Error loading staff report.');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, []);

  /**
   * Export Staff Report to PDF
   */
  const handleExportPDF = () => {
    if (!data || !data.by_department) return;

    const title = 'Staff Allocation & HR Payroll Report';
    const headers = ['Department', 'Staff Count', 'Monthly Payroll Outlay (Rs.)'];
    const rows = data.by_department.map((d) => [
      d.department || 'General',
      d.staff_count || 0,
      `Rs. ${parseFloat(d.department_payroll || 0).toLocaleString()}`
    ]);

    exportToPDF(title, headers, rows, school?.name);
  };

  const summary = data?.summary || { total_staff: 0, active_staff: 0, inactive_staff: 0, total_payroll: 0 };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/reports" className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center font-bold transition">
              ←
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Staff & HR Payroll Report</h1>
              <p className="text-xs text-slate-500">Departmental allocation & payroll budget summary</p>
            </div>
          </div>

          <button
            onClick={handleExportPDF}
            disabled={loading || !data}
            className="bg-primary hover:bg-blue-600 text-white font-bold px-5 py-2.5 rounded-xl text-xs shadow-md transition disabled:opacity-50 flex items-center gap-2"
          >
            📄 Export PDF Report
          </button>
        </div>
      </header>

      {/* Main Body */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {error && (
          <div className="p-4 bg-red-50 text-red-700 border border-red-200 rounded-2xl text-sm font-semibold">
            {error}
          </div>
        )}

        {/* 4 Stats Cards Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Total Staff</span>
            <span className="text-2xl font-black text-slate-900">{loading ? '...' : summary.total_staff}</span>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Active Staff</span>
            <span className="text-2xl font-black text-emerald-600">{loading ? '...' : summary.active_staff}</span>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Inactive Staff</span>
            <span className="text-2xl font-black text-red-600">{loading ? '...' : summary.inactive_staff}</span>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Total Monthly Payroll</span>
            <span className="text-2xl font-black text-indigo-600">Rs. {loading ? '...' : parseFloat(summary.total_payroll || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}</span>
          </div>
        </div>

        {/* Department Breakdown Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-slate-900 text-base">Departmental Staff Breakdown</h2>
            <span className="text-xs text-slate-400 font-semibold">{data?.by_department?.length || 0} Departments</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-sans">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase">
                  <th className="py-3.5 px-6">Department</th>
                  <th className="py-3.5 px-6 text-center">Staff Count</th>
                  <th className="py-3.5 px-6 text-right">Monthly Payroll (Rs.)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-800 font-medium">
                {loading ? (
                  <tr>
                    <td colSpan="3" className="py-12 text-center text-slate-400 font-medium">Loading department statistics...</td>
                  </tr>
                ) : !data?.by_department || data.by_department.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="py-12 text-center text-slate-400 font-medium">No department records found.</td>
                  </tr>
                ) : (
                  data.by_department.map((d, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/80 transition">
                      <td className="py-3.5 px-6 font-bold text-slate-900 text-sm capitalize">{d.department || 'General'}</td>
                      <td className="py-3.5 px-6 text-center font-bold text-slate-900">{d.staff_count}</td>
                      <td className="py-3.5 px-6 text-right font-mono font-bold text-indigo-600">Rs. {parseFloat(d.department_payroll || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </main>
    </div>
  );
};

export default StaffReportPage;
